import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, UserIcon } from '@heroicons/react/24/outline';
import { useHeader } from '../../contexts/HeaderContext';
import { useStudentsList, getGenderText } from '../../hooks/useStudentsList';

const CourseStudents: React.FC = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const courseId = queryParams.get('courseId');
    const programId = queryParams.get('programId');
    const { t } = useTranslation();
    const { setHeaderTheme } = useHeader();
    const { students, course, loading, error } = useStudentsList(courseId, programId);
    const navigate = useNavigate();

    useEffect(() => {
        // 设置透明背景和白色文字
        setHeaderTheme({
            bgColor: 'bg-transparent',
            textColor: 'text-white'
        });
        
        return () => {
            // 恢复默认样式
            setHeaderTheme({
                bgColor: 'bg-white',
                textColor: 'text-[#142b89]'
            });
        };
    }, [setHeaderTheme]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-800 to-blue-600 pt-20">
            {/* 顶部背景 */}
            <div className="relative w-full overflow-hidden">
                <div className="absolute inset-0 bg-[url('/images/background.jpg')] bg-cover bg-center opacity-10"></div>
                
                {/* 内容层 */}
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 z-10">
                    <div className="flex items-center mb-6">
                        <button 
                            onClick={() => navigate(-1)}
                            className="text-blue-200 hover:text-white mr-4"
                        >
                            <ArrowLeftIcon className="h-5 w-5" />
                        </button>
                        <motion.h1 
                            className="text-3xl font-bold text-white"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            {course ? course.name : t('course.loading')}
                        </motion.h1>
                    </div>
                    
                    <motion.div 
                        className="text-blue-200 mb-8"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                    >
                        <h2 className="text-xl">{t('teacher.courseStudents')}</h2>
                        {!loading && !error && (
                            <p className="mt-2">
                                {t('teacher.totalStudents')}: {students.length}
                            </p>
                        )}
                    </motion.div>
                </div>
            </div>
            
            {/* 主要内容区域 */}
            <div className="relative pb-20 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
                    {loading ? (
                        <div className="p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                            <div className="flex justify-center items-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-transparent"></div>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                            <div className="text-center py-8">
                                <p className="text-red-300">{error}</p>
                            </div>
                        </div>
                    ) : students.length === 0 ? (
                        <div className="p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                            <div className="text-center py-8">
                                <p className="text-blue-100">{t('teacher.noStudentsInCourse')}</p>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {students.map((student, index) => (
                                <motion.div 
                                    key={student.id}
                                    className="bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden shadow-lg border border-white/10"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: index * 0.05 }}
                                >
                                    <div className="p-6">
                                        <div className="flex items-center mb-4">
                                            {student.avatar ? (
                                                <img 
                                                    src={student.avatar} 
                                                    alt={student.firstName}
                                                    className="w-16 h-16 rounded-full object-cover mr-4 border-2 border-blue-300"
                                                />
                                            ) : (
                                                <div className="w-16 h-16 rounded-full bg-blue-700 flex items-center justify-center mr-4 border-2 border-blue-300">
                                                    <UserIcon className="h-8 w-8 text-blue-200" />
                                                </div>
                                            )}
                                            <div>
                                                <div className="flex flex-col">
                                                    <h3 className="text-lg font-semibold text-white">
                                                        {student.firstName} {student.lastName}
                                                        {student.chineseName && ` (${student.chineseName})`}
                                                    </h3>
                                                    <div className="flex flex-wrap gap-2 mt-1">
                                                        {student.gender && (
                                                            <span className="text-sm text-blue-200">
                                                                {getGenderText(student.gender, t)}
                                                            </span>
                                                        )}
                                                        {student.age > 0 && (
                                                            <span className="text-sm text-blue-200">
                                                                {student.age} {t('student.yearsOld')}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {(student.parentFirstName || student.parentLastName || student.parentPhone) && (
                                            <div className="mt-4 pt-4 border-t border-blue-700/30">
                                                <h4 className="text-sm font-medium text-blue-100 mb-2">{t('student.parentInfo')}</h4>
                                                {(student.parentFirstName || student.parentLastName) && (
                                                    <p className="text-sm text-blue-200 mb-1">
                                                        <span className="opacity-70">{t('student.parentName')}:</span> {student.parentFirstName} {student.parentLastName}
                                                    </p>
                                                )}
                                                {student.parentPhone && (
                                                    <p className="text-sm text-blue-200">
                                                        <span className="opacity-70">{t('student.parentPhone')}:</span> {student.parentPhone}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CourseStudents; 